import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {List, Map} from 'immutable';
import {parse} from 'papaparse';

import {FileInput} from '@albert-io/atomic';

import {csvHeaderValues} from '../shared';

/**
 * Parses a CSV file into an array of entries
 *
 * @param {File} file - A file object
 * @returns {Array} records - An array of arrays
 */
export function sanitizeUpload(file) {
  const records = parse(file, {skipEmptyLines: 'greedy'}).data;

  const firstRow = records[0];
  const hasHeader = firstRow.some((entry) => csvHeaderValues.includes(entry));

  if (hasHeader) {
    records.shift();
  }

  return records;
}

/**
 * Parses the raw array into an Immutable data structure our Record Manager can understand
 *
 * @param {Array} records - An array of arrays
 * @returns {List} parsedRecords - A List of keyed Maps (called "entries") derived from the array of arrays
 */
export function parseRecords(records) {
  return records.reduce((prev, curr, id) => {
    const [lastName, firstName, email, username, password] = curr;
    return prev.push(
      Map({
        email,
        firstName,
        id, // ID is provided to give each Immutable Map a unique signature
        lastName,
        password,
        username
      })
    );
  }, List());
}

const CreateAccountsFileInput = ({className, onChange}) => {
  const [error, setError] = useState(null);
  const [csv, setCsv] = useState(null);

  const handleChange = useCallback(
    async (e) => {
      const records = sanitizeUpload(e.target.result);
      const parsed = parseRecords(records);

      const tooFewRecords = parsed.size === 0;
      const tooManyRecords = parsed.size > 3000;
      if (tooFewRecords) {
        setError(
          'Your file must contain at least one entry. Please use the template available at the link above and try again.'
        );
        return;
      }
      if (tooManyRecords) {
        setError(
          'Too many records. You may create a maximum of 3000 accounts at once from a CSV file.'
        );
        return;
      }
      onChange(parsed);
    },
    [onChange]
  );

  useEffect(() => {
    if (csv) {
      const filenameToArray = csv.name.split('.');
      const extension = filenameToArray[filenameToArray.length - 1];
      const fileType = csv.type;
      if (fileType !== 'text/csv' && extension !== 'csv') {
        setError(
          `The file ${csv.name} that you uploaded is not a valid file type. Please make sure your file is saved as a *.csv file and try again.`
        );
      } else {
        setError(null);

        // 1. Create a new FileReader instance
        const reader = new FileReader();

        // 2. Define its miscellaneous handlers
        reader.onload = handleChange;
        reader.onerror = () =>
          setError(
            'There was a problem with processing your upload. Please use the template available at the link above and try again.'
          );

        // 3. Initiate the read
        reader.readAsText(csv, 'UTF-8');
      }
    }
  }, [csv, handleChange]);

  return (
    <FileInput
      accept='text/csv'
      className={className}
      error={!!error}
      errorMessage={error}
      onChange={(e) => setCsv(e.target.files[0])}
      placeholder='Upload a .csv file'
    />
  );
};

CreateAccountsFileInput.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

export default CreateAccountsFileInput;
